import React, { useState } from 'react';
import { FaMapMarkerAlt, FaUniversity, FaGlassMartini, FaMicrophone, FaHeart, FaUsers, FaChartLine, FaChevronDown, FaChevronUp, FaStar } from 'react-icons/fa';

const CommunitySection: React.FC = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowScrollIndicator(target.scrollTop > 20);
  };

  const sections = [
    {
      number: 1,
      icon: FaMapMarkerAlt,
      title: 'Active on Campuses & In Venues',
      description: 'CollectedAF has been deployed across multiple high-density, high-interaction environments:',
      subsections: [
        {
          icon: FaUniversity,
          title: 'University Campuses',
          items: [
            'University of Utah',
            'Boise State University',
            'Student housing, rec centers, libraries, cafeterias',
            'Thousands of proximity interactions generated in real-world settings'
          ]
        },
        {
          icon: FaGlassMartini,
          title: 'Local Bars & Social Venues',
          description: 'We\'ve partnered with local bars and nightlife spots where users:',
          items: [
            'Meet people instantly',
            'Join Popins',
            'Share attributes',
            'Build collections in real time'
          ],
          note: 'Venues see increased engagement, dwell time, and return visits.'
        },
        {
          icon: FaMicrophone,
          title: 'Conferences & Events',
          description: 'CollectedAF activates micro-communities instantly at high-traffic events, enabling:',
          items: [
            'Real-time networking',
            'Location-based Popins',
            'Live audience engagement',
            'Spontaneous discovery'
          ],
          note: 'This shows our platform works in environments where traditional social apps fall flat.'
        }
      ]
    },
    {
      number: 2,
      icon: FaHeart,
      title: 'Why Now',
      description: 'The world is shifting toward live, real-world interaction, and CollectedAF sits perfectly at the intersection of three massive trends:',
      trends: [
        {
          number: 1,
          title: 'Gen Z wants real connection, not curated feeds',
          description: 'They crave authenticity, proximity, and shared moments — not algorithmic noise.'
        },
        {
          number: 2,
          title: 'IRL experiences are booming',
          description: 'Campuses, concerts, co-working, nightlife — people want to connect with the humans around them.'
        },
        {
          number: 3,
          title: 'Venues need real-time digital engagement',
          description: 'Bars, campuses, events, and businesses want:',
          items: [
            'Foot-traffic visibility',
            'Live engagement tools',
            'Targeted offers',
            'Community-building features'
          ],
          note: 'CollectedAF delivers what no traditional social platform or CRM can.'
        }
      ]
    },
    {
      number: 3,
      icon: FaStar,
      title: 'Social Proof & Early Momentum',
      description: 'Across our deployments, users consistently describe CollectedAF as:',
      userQuotes: [
        '"The easiest way to meet people in real life."',
        '"A social icebreaker that actually works."',
        '"Networking without the awkwardness."'
      ],
      venueReports: {
        title: 'Venues report:',
        items: [
          'Higher customer interaction',
          'Longer stays',
          'Increased engagement in Popins',
          'Positive customer sentiment',
          'Strong repeat interest'
        ]
      },
      campusReports: {
        title: 'Campuses report:',
        items: [
          'Students connecting more',
          'Positive feedback from ambassadors',
          'Organic social sharing',
          'Usage across dorms, lounges, and events'
        ]
      }
    }
  ];

  return (
    <div className="z-20 flex-shrink-0 py-8">
      <div className="relative min-h-[350px] lg:h-[calc(100vh-8rem)]">
        {/* Background Giant C */}
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[30rem] opacity-25 pointer-events-none"
          style={{ 
            fontFamily: '"Anton", sans-serif',
            background: 'linear-gradient(135deg, #14b8a6 0%, #fbbf24 45%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.35))'
          }}
        >
          C
        </div>
        
        {/* Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="colorful-ring bg-BLACK/96 backdrop-blur-2xl rounded-3xl p-6 lg:p-12 w-full h-auto lg:h-full transition-colors shadow-2xl flex flex-col">
            <h2 className="text-3xl lg:text-4xl font-bold text-WHITE mb-3 text-center">Traction & Real-World Presence</h2>
            <div className="bg-WHITE/10 backdrop-blur-xl rounded-2xl px-6 py-3 mb-6 mx-auto w-fit border border-WHITE/15 shadow-lg">
              <p
                className="text-lg font-bold text-center leading-relaxed"
                style={{
                  background: 'linear-gradient(90deg, #000000 0%, #0066cc 50%, #000000 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                CollectedAF is already proving itself where real connection matters most.
              </p>
            </div>
            
            {/* Scroll Indicator */}
            <div className={`absolute top-32 left-0 right-0 h-12 bg-gradient-to-b from-WHITE/20 to-transparent pointer-events-none transition-opacity duration-300 z-10 ${showScrollIndicator ? 'opacity-100' : 'opacity-0'}`}>
              <div className="flex justify-center pt-2">
                <FaChevronUp className="text-WHITE/60 animate-bounce" size={24} />
              </div>
            </div>
            
            {/* Accordions - Scrollable */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-6 pr-2" onScroll={handleScroll}>
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <div key={index} className="bg-WHITE/5 rounded-xl border border-WHITE/10 hover:border-WHITE/30 transition-colors">
                    {/* Accordion Header */}
                    <button
                      onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                      className="w-full flex items-center gap-3 p-4 text-left"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-WHITE/20 backdrop-blur-md flex items-center justify-center border-2 border-WHITE/25">
                        <Icon className="text-WHITE" size={20} />
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-WHITE/20 border border-WHITE/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-WHITE text-lg font-bold">{section.number}</span>
                        </div>
                        <h4 className="text-WHITE text-lg font-bold">{section.title}</h4>
                      </div>
                      {openAccordion === index ? (
                        <FaChevronUp className="text-WHITE/60" size={20} />
                      ) : (
                        <FaChevronDown className="text-WHITE/50" size={20} />
                      )}
                    </button>
                    
                    {/* Accordion Content */}
                    {openAccordion === index && (
                      <div className="px-4 pb-4 pt-0">
                        <p className="text-WHITE/70 text-sm mb-4 leading-relaxed">{section.description}</p>
                        
                        {/* Subsections for Active Deployments */}
                        {section.subsections && (
                          <div className="space-y-4">
                            {section.subsections.map((sub, idx) => {
                              const SubIcon = sub.icon;
                              return (
                                <div key={idx} className="bg-WHITE/5 rounded-lg p-4 border border-WHITE/10">
                                  <div className="flex items-center gap-2 mb-2">
                                    <SubIcon className="text-WHITE" size={18} />
                                    <h5 className="text-WHITE font-bold text-base">{sub.title}</h5>
                                  </div>
                                  {sub.description && (
                                    <p className="text-WHITE/60 text-sm mb-2">{sub.description}</p>
                                  )}
                                  <ul className="space-y-1">
                                    {sub.items.map((item, i) => (
                                      <li key={i} className="text-WHITE/60 text-sm flex items-start gap-2">
                                        <span className="text-WHITE/50 mt-1">•</span>
                                        <span>{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                  {sub.note && (
                                    <p className="text-WHITE/50 text-xs italic mt-2">{sub.note}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Trends for Why Now */}
                        {section.trends && (
                          <div className="space-y-3">
                            {section.trends.map((trend, idx) => (
                              <div key={idx} className="bg-WHITE/5 rounded-lg p-4 border border-WHITE/10">
                                <div className="flex items-start gap-3 mb-2">
                                  <div className="w-8 h-8 rounded-full bg-WHITE/20 border border-WHITE/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-WHITE text-lg font-bold">{trend.number}</span>
                                  </div>
                                  <div className="flex-1">
                                    <h5 className="text-WHITE font-bold text-base mb-1">{trend.title}</h5>
                                    <p className="text-WHITE/70 text-sm">{trend.description}</p>
                                    {trend.items && (
                                      <ul className="space-y-1 mt-2">
                                        {trend.items.map((item, i) => (
                                          <li key={i} className="text-WHITE/60 text-sm flex items-center gap-2">
                                            <span className="text-WHITE/50">•</span>
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                    {trend.note && (
                                      <p className="text-WHITE/50 text-xs italic mt-2">{trend.note}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Social Proof */}
                        {section.userQuotes && (
                          <div className="space-y-4">
                            {/* User Quotes */}
                            <div className="bg-WHITE/10 rounded-lg p-4 border border-WHITE/15">
                              {section.userQuotes.map((quote, idx) => (
                                <p key={idx} className="text-WHITE/90 text-sm italic mb-2 last:mb-0">
                                  {quote}
                                </p>
                              ))}
                            </div>
                            
                            {/* Venue Reports */}
                            <div className="bg-WHITE/5 rounded-lg p-4 border border-WHITE/10">
                              <div className="flex items-center gap-2 mb-2">
                                <FaChartLine className="text-WHITE" size={16} />
                                <h5 className="text-WHITE font-bold text-sm">{section.venueReports.title}</h5>
                              </div>
                              <ul className="space-y-1">
                                {section.venueReports.items.map((item, i) => (
                                  <li key={i} className="text-WHITE/60 text-sm flex items-center gap-2">
                                    <span className="text-WHITE/50">✓</span>
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            {/* Campus Reports */}
                            <div className="bg-WHITE/5 rounded-lg p-4 border border-WHITE/10">
                              <div className="flex items-center gap-2 mb-2">
                                <FaUniversity className="text-WHITE" size={16} />
                                <h5 className="text-WHITE font-bold text-sm">{section.campusReports.title}</h5>
                              </div>
                              <ul className="space-y-1">
                                {section.campusReports.items.map((item, i) => (
                                  <li key={i} className="text-WHITE/60 text-sm flex items-center gap-2">
                                    <span className="text-WHITE/50">✓</span>
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Takeaway Section */}
            <div className="bg-WHITE/10 backdrop-blur-xl rounded-xl p-6 border border-WHITE/15 shadow-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-WHITE/20 backdrop-blur-md flex items-center justify-center border-2 border-WHITE/25">
                  <FaUsers className="text-WHITE" size={24} />
                </div>
                <h3 className="text-WHITE text-2xl font-bold">The Takeaway</h3>
              </div>
              
              <div className="bg-WHITE/10 backdrop-blur-xl rounded-2xl px-6 py-4 border border-WHITE/15 shadow-lg">
                <p
                  className="text-base font-bold text-center leading-relaxed"
                  style={{
                    background: 'linear-gradient(90deg, #000000 0%, #0066cc 50%, #000000 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  People are ready for real connection again — and CollectedAF is already proving it in the real world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunitySection;
